/**
 * Direction D ("Sidebar + Brand") style tokens — plain constants, not a React
 * component, so this pass and later screen restyles all read colors/radii/
 * shadows/fonts from one place instead of re-typing hexes. Every value is a
 * plain string/number so the existing `S = {...} satisfies
 * Record<string, React.CSSProperties>` objects in Dashboard.tsx / TeamCalendar.tsx
 * / AppShell.tsx can reference these inside their own CSSProperties literals
 * without breaking that convention (this file introduces no new one).
 *
 * Source of truth: the two approved mockups (SidebarBrand.dc.html,
 * SidebarBrandMobile.dc.html) — every hex below started as a copy from them,
 * not eyeballed. Adversarial review (2026-09-04) found a handful of the
 * mockups' own text colors failing WCAG AA on the backgrounds they actually
 * render on; those tokens (amberTileLabel, textMuted, textFaint) were
 * darkened just enough to clear 4.5:1, called out inline below. chipBrandBg/
 * chipTealBg were added here to centralize hex values Dashboard.tsx and
 * TeamCalendar.tsx had been hand-typing independently.
 */

export const color = {
  brandBlue: '#0B46C4',
  amber: '#D97706',
  amberDark: '#92400E',
  amberBg: '#FEF3E2',
  amberBorder: '#F5DEB0',
  // A11y fix (adversarial review, 2026-09-04): the mockup's #B08650 computes
  // to ~3.0:1 on amberBg — below WCAG AA's 4.5:1 for the 11.5px bold
  // "OVER ALLOTMENT" tile label. Darkened to ~5.3:1 while staying lighter
  // than amberDark (the big number beside it), preserving the label/value
  // hierarchy.
  amberTileLabel: '#7F603A',
  teal: '#0F6E64',
  pageBg: '#F7F8FA',
  cardBg: '#FFFFFF',
  cardBorder: '#ECEDF1',
  ink: '#20242C',
  // A11y fix (adversarial review, 2026-09-04): the mockup's #8A93A3 computes
  // to ~3.16:1 on white/cardBg — below WCAG AA's 4.5:1 for the normal-size
  // text it's used on (dashboardDate, tdSub, logDate, logMeta). Darkened to
  // ~4.8:1, one notch lighter than textSubtle to keep the muted/subtle
  // hierarchy intact.
  textMuted: '#6B7280',
  // A11y fix (adversarial review, 2026-09-04): the mockup's #9AA3AF computes
  // to ~2.55:1 on white/cardBg — badly below WCAG AA's 4.5:1, and this token
  // drives real, persistent text (every inactive bottom-tab-bar label, every
  // table header, every tile label). Darkened to ~4.6:1, the lightest of the
  // three text tiers that still clears AA.
  textFaint: '#6F757E',
  textSubtle: '#5B6472',
  pillNeutralBg: '#F1F3F7',
  pendingDash: '#7C93D8',
  closureStripeA: '#F1F2F5',
  closureStripeB: '#E7E9ED',
  // Brand-blue and teal light-tint pill/chip backgrounds — centralized here
  // (adversarial review, 2026-09-04) instead of the hex being hand-typed in
  // both Dashboard.tsx and TeamCalendar.tsx, per this file's own "one source
  // of truth" rule.
  chipBrandBg: '#E8F0FE',
  chipTealBg: '#E3F0EE',
} as const;

export const radius = {
  cardDesktop: '20px',
  cardMobile: '16px',
  tileDesktop: '18px',
  tileMobile: '16px',
  calendarCellDesktop: '14px',
  calendarCellMobile: '8px',
  pill: '999px',
} as const;

// Desktop-only per spec — mobile cards may skip it (border carries enough
// weight there, and it is one less thing for a phone GPU to composite).
export const shadow = {
  card: '0 4px 14px rgba(16,24,40,0.05), 0 1px 3px rgba(16,24,40,0.04)',
} as const;

export const font = {
  display: "'Space Grotesk', system-ui, sans-serif",
  body: "system-ui, -apple-system, 'Segoe UI', sans-serif",
} as const;

export const layout = {
  sidebarWidth: '224px',
  bottomTabHeight: '64px',
} as const;
